import colors from "@/lib/theme/colors"
import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import Image from "next/image"

// Figma lays the row out physically (pause left, arrows right), so the row opts
// out of the page's RTL flow. Mobile: one row under the CTAs. Desktop: the row
// is just a positioning shell and both children sit absolutely on the hero.
const StyledControls = styled.div`
  direction: ltr;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: contents;
  }
`

// Visibility is per breakpoint because desktop and mobile can resolve to
// different media (e.g. a desktop video over a mobile image).
const PauseSlot = styled.div<{ $onMobile: boolean; $onDesktop: boolean }>`
  display: ${({ $onMobile }) => ($onMobile ? "flex" : "none")};
  ${(props) => props.theme.breakpoints.up("md")} {
    display: ${({ $onDesktop }) => ($onDesktop ? "flex" : "none")};
    position: absolute;
    left: 40px;
    bottom: 64px;
  }
`

// Desktop: spans the hero width 40px in from each edge, vertically centered;
// pointer-events are re-enabled on the buttons only so the gap stays clickable.
const ArrowsGroup = styled.div`
  display: flex;
  gap: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    position: absolute;
    top: 50%;
    left: 40px;
    right: 40px;
    transform: translateY(-50%);
    justify-content: space-between;
    pointer-events: none;
  }
`

export const ArrowButton = styled(IconButton)`
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid ${colors.white};
  border-radius: 999px;
  pointer-events: auto;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 40px;
    height: 40px;
  }
`

const ArrowIcon = styled.span<{ $flipped?: boolean }>`
  display: flex;
  transform: ${({ $flipped }) => ($flipped ? "rotate(180deg)" : "none")};
  .desktop-icon {
    display: none;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    .mobile-icon {
      display: none;
    }
    .desktop-icon {
      display: block;
    }
  }
`

const StyeldIconButton = styled(IconButton)`
  min-width: 0;
  padding: 0;
  z-index: 10000;
`

type ButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void

type Props = {
  showArrows: boolean
  showPauseOnMobile: boolean
  showPauseOnDesktop: boolean
  isPaused: boolean
  handleResume: ButtonClick
  handlePause: ButtonClick
  handlePrev: ButtonClick
  handleNext: ButtonClick
}

export function Arrow({ flipped }: { flipped?: boolean }): React.ReactElement {
  return (
    <ArrowIcon $flipped={flipped}>
      <Image
        className="mobile-icon"
        src="/assets/heroArrowSmall.svg"
        width={16}
        height={16}
        alt=""
      />
      <Image className="desktop-icon" src="/assets/heroArrow.svg" width={24} height={24} alt="" />
    </ArrowIcon>
  )
}

export default function HeroControls({
  showArrows,
  showPauseOnMobile,
  showPauseOnDesktop,
  isPaused,
  handlePause,
  handleResume,
  handlePrev,
  handleNext,
}: Props): React.ReactElement {
  return (
    <StyledControls>
      <PauseSlot $onMobile={showPauseOnMobile} $onDesktop={showPauseOnDesktop}>
        {isPaused ? (
          <StyeldIconButton onClick={(e) => handleResume(e)}>
            <Image src={"/assets/playbutton.svg"} width={24} height={24} alt="play" />
          </StyeldIconButton>
        ) : (
          <StyeldIconButton onClick={(e) => handlePause(e)}>
            <Image src={"/assets/pausebutton.svg"} width={24} height={24} alt="pause" />
          </StyeldIconButton>
        )}
      </PauseSlot>
      {showArrows && (
        <ArrowsGroup>
          {/* RTL reading order: the left (←) arrow advances, the right (→) goes back */}
          <ArrowButton aria-label="הבאנר הבא" onClick={(e) => handleNext(e)}>
            <Arrow />
          </ArrowButton>
          <ArrowButton aria-label="הבאנר הקודם" onClick={(e) => handlePrev(e)}>
            <Arrow flipped />
          </ArrowButton>
        </ArrowsGroup>
      )}
    </StyledControls>
  )
}
